import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils/classnames";

interface NavigationRibbonItemProps {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  collapsed?: boolean;
  /** Navigation target. Omit + pass onClick for non-link actions (e.g. upgrade prompt). */
  href?: string;
  onClick?: () => void;
  /** Numeric badge (e.g. pending-review count). 0/undefined hides it. */
  badge?: number;
  /** Show a quiet PRO tag (entitlement-gated destination). */
  pro?: boolean;
}

/**
 * A single row in the NavigationRibbon. Collapsed → icon-only with a native
 * tooltip; expanded → icon + label (+ optional badge / PRO tag). Active state is a
 * brand-cyan tint, never a heavy fill.
 */
export function NavigationRibbonItem({
  icon: Icon,
  label,
  active = false,
  collapsed = false,
  href,
  onClick,
  badge,
  pro = false,
}: NavigationRibbonItemProps) {
  const className = cn(
    "group relative flex items-center rounded-md transition-colors",
    collapsed ? "h-9 w-9 justify-center mx-auto" : "h-9 px-2.5 gap-2.5 w-full",
    active
      ? "bg-brand/10 text-brand ring-1 ring-inset ring-brand/20"
      : "text-muted-foreground hover:text-foreground hover:bg-muted",
  );

  const inner = (
    <>
      <Icon size={18} className="flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left text-sm font-medium">
            {label}
          </span>
          {pro && (
            <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand bg-brand/10">
              Pro
            </span>
          )}
          {badge !== undefined && badge > 0 && (
            <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-brand text-brand-foreground text-[10px] font-semibold">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
      {/* collapsed: a small dot stands in for the badge */}
      {collapsed && badge !== undefined && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-brand ring-2 ring-card" />
      )}
    </>
  );

  if (href) {
    return (
      <Link to={href} className={className} title={collapsed ? label : undefined} aria-current={active ? "page" : undefined}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} title={collapsed ? label : undefined}>
      {inner}
    </button>
  );
}
